/**
 * The player import's browser half (ADR 0019, docs/specs/player-import.md §4, §9): the file checks, the split
 * into parts, the combined review, and the run's sequencing. Pure, so `playerImport.test.js` can drive every
 * rule against a stub API; the modal only renders what these answer.
 */

import type {
  CommitPlayerImportRequest,
  PlayerImportCounts,
  PlayerImportIssue,
  PlayerImportPreview,
  PlayerImportResult,
  PlayerImportRow,
  PreviewPlayerImportRequest,
  RoomTemplate,
} from '@brush/api-client';

/** The template a part was read with (PI71, PI78). */
export type PlayerImportTemplate = PlayerImportPreview['template'];
import { listPhrase } from './columns.ts';
import { isBlankRow, parseCsv } from './csv.ts';

/** PI49: the whole file. Ten parts, about 18 times Dev's largest room. */
export const MAX_FILE_ROWS = 25_000;
/** PI49: 10 MB, in the server's unit — `MaxBodyBytes` is 2 × 1024 × 1024. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
/** PI2: the server's `PreviewPlayerImportRequest.MaxDataRows`. */
export const MAX_PART_ROWS = 2_500;
/** PI50: a part ends early where its body would pass this, so PI3's 2 MB is never met. */
export const MAX_PART_BYTES = 1.5 * 1024 * 1024;

/** PI49's checks that need no reading: the extension and the size. Null when the file may be read. */
export function checkFileMeta(name: string, size: number): string | null {
  if (!name.toLowerCase().endsWith('.csv')) return 'Choose a .csv file. Save the sheet as CSV first.';
  if (size > MAX_FILE_BYTES) return 'The file is over 10 MB, the most one import reads. Split it into smaller files.';
  return null;
}

/** A file read for import: its heading row, and every line after it as the server will number them. */
export interface ImportFile {
  /** The first non-blank line, which PI5 makes the heading. */
  heading: string[];
  /** Its spreadsheet row number, 1-based. */
  headingRow: number;
  /** Every line after the heading, blank ones kept so the server's row numbers are the file's; trailing blank lines dropped. */
  body: string[][];
  /** Non-blank lines in `body`. */
  dataRows: number;
}

export type ReadResult = { ok: true; file: ImportFile } | { ok: false; problem: string };

/**
 * PI1, PI4, PI5, PI49: the text read into a heading and the lines after it, or why it cannot be imported. Which
 * column is the card is the server's to say (PI52): the browser holds no spellings, since a heading may carry the
 * room's template labels, and reading them here would be a second resolution of the template.
 */
export function readImportFile(text: string): ReadResult {
  const lines = parseCsv(text);
  const headingIndex = lines.findIndex((row) => !isBlankRow(row));
  if (headingIndex < 0) return { ok: false, problem: 'The file is empty.' };

  const body = lines.slice(headingIndex + 1);
  while (body.length > 0 && isBlankRow(body[body.length - 1])) body.pop();
  const dataRows = body.filter((row) => !isBlankRow(row)).length;
  if (dataRows === 0) return { ok: false, problem: 'The file has a heading row and no players under it.' };
  if (dataRows > MAX_FILE_ROWS) {
    return {
      ok: false,
      problem: `The file has ${dataRows.toLocaleString('en-US')} players; one import reads at most 25,000. Split it into smaller files.`,
    };
  }

  return { ok: true, file: { heading: lines[headingIndex], headingRow: headingIndex + 1, body, dataRows } };
}

/** A file line's spreadsheet row number: the heading's, plus how far below it the line sits. */
const rowNumberOf = (file: ImportFile, bodyIndex: number) => file.headingRow + 1 + bodyIndex;

/** `resultPlan.ts`'s `cardKey`: trimmed and lower-cased. Not the database's comparison (PI51, PI58). */
const cardKey = (card: string) => card.trim().toLowerCase();

const formatRow = (n: number) => n.toLocaleString('en-US');

/**
 * PI51: every card that repeats an earlier row's anywhere in the file, across all parts, naming both rows, as
 * file-level errors. `cardColumn` is the server's (PI52), 0-based. A blank card is left to the server
 * (`card_blank`), and so is a row the server already refused as a repeat within its part (PI13, `except`), so one
 * repeat is never reported and counted twice.
 */
export function repeatedCards(file: ImportFile, cardColumn: number, except: ReadonlySet<number> = new Set()): PlayerImportIssue[] {
  const firstSeen = new Map<string, number>();
  const problems: PlayerImportIssue[] = [];
  file.body.forEach((row, i) => {
    const card = (row[cardColumn] ?? '').trim();
    if (card === '') return;
    const at = rowNumberOf(file, i);
    const earlier = firstSeen.get(cardKey(card));
    if (earlier === undefined) firstSeen.set(cardKey(card), at);
    else if (!except.has(at)) {
      problems.push({
        level: 'error',
        code: 'card_repeated',
        message: `Card ${card} on row ${formatRow(at)} repeats row ${formatRow(earlier)}. Each card may appear once in the file.`,
        row: at,
        field: 'card_id',
      });
    }
  });
  return problems;
}

/** One request's worth of the file (PI50): the heading row, then its lines, and where they sit in the file. */
export interface ImportPart {
  part: number;
  parts: number;
  /** The spreadsheet row number of the part's first line after the heading. */
  firstRow: number;
  /** And of its last line. */
  lastRow: number;
  /** The heading row first, then the part's lines, blank ones kept. */
  rows: string[][];
  dataRows: number;
}

/**
 * What a commit's body carries besides its rows — the import id, the part's numbers and the two 64-character
 * hashes, with the JSON around them — allowed for generously, so the measure is a ceiling rather than a guess.
 */
const ENVELOPE_BYTES = 300;
const utf8 = new TextEncoder();
const jsonBytes = (row: readonly string[]) => utf8.encode(JSON.stringify(row)).length;

/**
 * PI50: the file in parts, in file order. A part ends at 2,500 lines after the heading — blank lines count, since
 * they are sent so the server numbers rows as the file does, and the server's 2,500 counts what it is sent — or
 * earlier where its body would pass 1.5 MB. A stretch holding no player is not sent: the next part's first row
 * number carries the file's numbering past it.
 */
export function splitIntoParts(file: ImportFile): ImportPart[] {
  const headingBytes = jsonBytes(file.heading) + 1;
  const chunks: { start: number; end: number; dataRows: number }[] = [];
  let start = 0;
  while (start < file.body.length) {
    let end = start;
    let bytes = ENVELOPE_BYTES + headingBytes;
    let dataRows = 0;
    while (end < file.body.length && end - start < MAX_PART_ROWS) {
      const next = jsonBytes(file.body[end]) + 1;
      if (end > start && bytes + next > MAX_PART_BYTES) break;
      bytes += next;
      if (!isBlankRow(file.body[end])) dataRows += 1;
      end += 1;
    }
    if (dataRows > 0) chunks.push({ start, end, dataRows });
    start = end;
  }

  return chunks.map((c, i) => ({
    part: i + 1,
    parts: chunks.length,
    firstRow: rowNumberOf(file, c.start),
    lastRow: rowNumberOf(file, c.end - 1),
    rows: [file.heading, ...file.body.slice(c.start, c.end)],
    dataRows: c.dataRows,
  }));
}

/** The parts' previews as one review (PI53). */
export interface ImportReview {
  /** The template the file was read with: the first part's, which PI77 holds every part to (PI78). */
  template: PlayerImportTemplate | null;
  counts: PlayerImportCounts;
  fileIssues: PlayerImportIssue[];
  rows: PlayerImportRow[];
}

const ZERO_COUNTS: PlayerImportCounts = {
  dataRows: 0, created: 0, updated: 0, unchanged: 0, conflicts: 0, errors: 0, warnings: 0, contactsFilled: 0,
};

/**
 * PI53: the parts' previews as one result — counts summed, rows in file order (the server numbers them for the
 * whole file), and a file-level issue once: every part carries the heading, so each reports the same ones.
 */
export function combineReview(previews: readonly PlayerImportPreview[]): ImportReview {
  const counts = { ...ZERO_COUNTS };
  for (const p of previews)
    for (const key of Object.keys(counts) as (keyof PlayerImportCounts)[]) counts[key] += p.counts[key];

  const seen = new Set<string>();
  const fileIssues = previews.flatMap((p) => p.fileIssues).filter((issue) => {
    const key = `${issue.level}|${issue.code}|${issue.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const rows = previews.flatMap((p) => p.rows).sort((a, b) => a.row - b.row);
  return { template: previews[0]?.template ?? null, counts, fileIssues, rows };
}

/** Adds file-level errors to a review, counting each: `Errors` counts file-level errors with error rows (§3). */
const withFileErrors = (review: ImportReview, issues: readonly PlayerImportIssue[]): ImportReview =>
  issues.length === 0 ? review : {
    ...review,
    counts: { ...review.counts, errors: review.counts.errors + issues.length },
    fileIssues: [...review.fileIssues, ...issues],
  };

/** PI7's refusal, worded as the server words it (`PlayerImport.CardColumnRequiredMessage`): the card's label and an example. */
function cardColumnRequired(template: PlayerImportTemplate | null): string {
  const label = template?.columns.find((c) => c.column === 'card_id')?.label ?? 'CardID';
  const example = label.trim().toLowerCase() === 'card id' ? 'Card' : 'Card ID';
  return `No column names the card. The card column's heading must be ${label} — or one of its alternative spellings, such as ${example}.`;
}

/**
 * PI51, PI52: once every part has answered, the repeat check over the card column the first part's preview
 * reports. With none reported there is no check to run, and the file stands refused as `card_column_required` —
 * the server's own issue where it sent one, which it always does (PI7); the browser's only where it did not, so
 * Import can never be offered over a file with no card column.
 */
export function checkRepeats(file: ImportFile, previews: readonly PlayerImportPreview[], review: ImportReview): ImportReview {
  const card = previews[0]?.columns.find((c) => c.column === 'card_id');
  if (!card) {
    if (review.fileIssues.some((i) => i.code === 'card_column_required')) return review;
    return withFileErrors(review, [{
      level: 'error', code: 'card_column_required', message: cardColumnRequired(previews[0]?.template ?? null), row: null, field: 'card_id',
    }]);
  }
  const serverSaw = new Set(review.rows.filter((r) => r.issues.some((i) => i.code === 'card_repeated')).map((r) => r.row));
  return asErrorRows(file, card.position - 1, review, repeatedCards(file, card.position - 1, serverSaw));
}

/** Which count a row's outcome is in, for the outcomes a repeat can take a row out of. */
const OUTCOME_COUNT: Partial<Record<string, keyof PlayerImportCounts>> = {
  create: 'created', update: 'updated', conflict: 'conflicts', unchanged: 'unchanged',
};

/**
 * PI51: a row repeating another part's card is an error and nothing else — what the server makes of a repeat within a
 * part (PI13). Each part previewed it as a card of its own, so it leaves the count its outcome was in, any field it
 * would have filled, and becomes an error row carrying the repeat, listed under Errors; a row the server did not list
 * (unchanged) is listed now. The repeat stays a file-level issue too, naming both rows, and is counted once.
 */
function asErrorRows(file: ImportFile, cardColumn: number, review: ImportReview, repeats: readonly PlayerImportIssue[]): ImportReview {
  if (repeats.length === 0) return review;
  const counts = { ...review.counts };
  const rows = [...review.rows];
  for (const issue of repeats) {
    const at = rows.findIndex((r) => r.row === issue.row);
    const listed = at < 0 ? null : rows[at];
    if (listed?.outcome === 'error') {
      rows[at] = { ...listed, issues: [...listed.issues, issue] };
      continue;
    }
    const was = listed ? OUTCOME_COUNT[listed.outcome] : 'unchanged';
    if (was) counts[was] -= 1;
    if (listed?.email === 'filled') counts.contactsFilled -= 1;
    if (listed?.phone === 'filled') counts.contactsFilled -= 1;
    counts.errors += 1;
    const cells = file.body[(issue.row ?? 0) - file.headingRow - 1] ?? [];
    const row: PlayerImportRow = {
      row: issue.row ?? 0, cardId: (cells[cardColumn] ?? '').trim(), outcome: 'error', changes: [],
      email: null, phone: null, issues: [...(listed?.issues ?? []), issue],
    };
    if (listed) rows[at] = row;
    else rows.push(row);
  }
  rows.sort((a, b) => a.row - b.row);
  return { ...review, counts, rows, fileIssues: [...review.fileIssues, ...repeats] };
}

/** PI78's lines: which template the file was read with, whether to link to Settings → Templates, and the columns. */
export interface ReadWith {
  sentence: string | null;
  linkToTemplates: boolean;
  columns: string;
}

/** The seven columns as the built-in read spells them (PI71 step 3), in PI65's order. */
const BUILT_IN_LABELS = ['CardID', 'FirstName', 'LastName', 'City', 'State', 'Email', 'Phone'];

/**
 * PI78: the review's line saying which template the file was read with — the room's, or the platform's with a way
 * to Settings → Templates to adapt it, or nothing for the built-in seven — and the columns the import read, by the
 * labels its headings were matched by. From the preview's `Template`, which is the authority.
 */
export function readWithLines(template: PlayerImportTemplate): ReadWith {
  const read = template.columns.filter((c) => c.included).map((c) => c.label);
  const columns = `Columns read: ${read.join(', ')}.`;
  if (template.source === 'room') return { sentence: "Read with this room's players template.", linkToTemplates: false, columns };
  if (template.source === 'platform') {
    return {
      sentence: "Read with the platform's players template — this room has not adapted it.",
      linkToTemplates: true,
      columns,
    };
  }
  return { sentence: null, linkToTemplates: false, columns };
}

/**
 * PI78: before a file is chosen, the columns the room's players template reads, from `useRoomTemplates` — a hint
 * only, resolved as the server will (PI71, PI72): the room's content where it holds every required column, else
 * every column of the platform template, else the built-in seven. Null while the templates are being read.
 */
export function templateHint(entries: readonly RoomTemplate[] | undefined): string | null {
  if (!entries) return null;
  const players = entries.find((e) => e.template.purpose === 'import_players');
  if (!players) return BUILT_IN_LABELS.join(', ');
  const fields = players.template.fields;
  const keys = players.content?.fieldKeys ?? null;
  const complete = keys !== null && fields.filter((f) => f.required).every((f) => keys.includes(f.key));
  return fields.filter((f) => !complete || keys.includes(f.key)).map((f) => f.label).join(', ');
}

/**
 * The file step's line: the columns (PI78's hint, when the room's templates have been read), what happens to cards the
 * room already holds, and the contact rule. It names no room: the dialog's subtitle already does.
 */
export function fileStepLine(hint: string | null): string {
  return (
    `A CSV with a heading row naming the columns${hint ? ` — ${hint} —` : ','} in any order; only the card is required. ` +
    'Cards this room already holds are updated; new ones are created. ' +
    'A contact is filled only where the card has none, and never overwritten.'
  );
}

/**
 * §9 step 2: a long list of file-level issues — a file of many repeated cards (PI51) names each — shows its first
 * twenty, which say what is wrong, and how many more there are.
 */
export function capped<T>(items: readonly T[], limit = 20): { shown: T[]; more: string | null } {
  if (items.length <= limit) return { shown: [...items], more: null };
  return { shown: items.slice(0, limit), more: `And ${(items.length - limit).toLocaleString('en-US')} more.` };
}

/** §9 step 3's button: its words, and why it is disabled, or null when it may be pressed. */
export interface ImportAction {
  label: string;
  disabledReason: string | null;
}

/** The rows a commit writes: new cards, updated ones, and conflict rows, whose other changes still apply (PI25). */
export const changeCount = (counts: PlayerImportCounts) => counts.created + counts.updated + counts.conflicts;

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

export function importAction(review: ImportReview): ImportAction {
  const n = changeCount(review.counts);
  const label = `Import ${plural(n, 'change', 'changes')}`;
  if (review.counts.errors > 0) {
    return {
      label,
      disabledReason: `The file has ${plural(review.counts.errors, 'error', 'errors')}. Correct the file, then choose it again; nothing is imported while one stands.`,
    };
  }
  if (n === 0) return { label, disabledReason: 'Nothing in this file would change a card.' };
  return { label, disabledReason: null };
}

/** The review table's filters (§9 step 2), one per outcome a listed row can have. */
export type ReviewFilter = 'error' | 'create' | 'update' | 'conflict' | 'unchanged';

/**
 * Contact fields the file gave and the import ignored (PI17). Not one of the server's counts: read off the listed
 * rows, which hold every such field — an ignored value is a warning, and a row with a warning is listed (PI27).
 */
const fieldsIgnored = (review: ImportReview) =>
  review.rows.reduce((n, r) => n + (r.email === 'ignored' ? 1 : 0) + (r.phone === 'ignored' ? 1 : 0), 0);

/** §9 step 2's line across the top. `contactsFilled` counts fields, so it reads "fields filled". */
export function reviewCountsLine(review: ImportReview): string {
  const c = review.counts;
  return [
    `${c.created.toLocaleString('en-US')} new`,
    `${c.updated.toLocaleString('en-US')} updating`,
    plural(c.contactsFilled, 'field filled', 'fields filled'),
    plural(c.conflicts, 'conflict', 'conflicts'),
    plural(fieldsIgnored(review), 'field ignored', 'fields ignored'),
    `${c.unchanged.toLocaleString('en-US')} unchanged`,
    plural(c.errors, 'error', 'errors'),
  ].join(' · ');
}

/** §9 step 2: above the counts, for a file of more than one part. */
export function partsNotice(parts: number): string | null {
  if (parts <= 1) return null;
  return `Sent in ${parts} parts. Each part is saved on its own; if one fails, the parts before it stay saved and running this file again finishes the rest.`;
}

const FIELD_WORDS: Record<string, string> = {
  firstName: 'First name', lastName: 'Last name', city: 'City', state: 'State', avatar: 'Initials',
};

/**
 * One row's changes in words (§9 step 2). Names, city and state carry before and after; a contact carries only
 * what happened to it — the preview holds no contact value (PI28), and this never invents one.
 */
export function rowChanges(row: PlayerImportRow): string[] {
  const words = row.changes.map((c) => {
    const field = FIELD_WORDS[c.field] ?? c.field;
    return c.before === null || c.before === '' ? `${field} set to ${c.after ?? ''}` : `${field} ${c.before} → ${c.after ?? ''}`;
  });
  const contact = (status: string | null | undefined, label: string, not: string) => {
    if (status === 'filled') words.push(`${label} filled`);
    else if (status === 'conflict') words.push(`${label} kept — the file has a different one`);
    else if (status === 'ignored') words.push(`${label} ignored — ${not}`);
  };
  contact(row.phone, 'Phone', 'not a phone number');
  contact(row.email, 'Email', 'not an email address');
  return words;
}

const FILTERS: { filter: ReviewFilter; label: string }[] = [
  { filter: 'error', label: 'Errors' },
  { filter: 'create', label: 'New' },
  { filter: 'update', label: 'Updating' },
  { filter: 'conflict', label: 'Conflicts' },
  { filter: 'unchanged', label: 'Unchanged, with a warning' },
];

/** §9 step 2's filter chips: one per outcome the listed rows hold, with how many. */
export function reviewFilters(review: ImportReview): { filter: ReviewFilter; label: string; count: number }[] {
  return FILTERS.map((f) => ({ ...f, count: review.rows.filter((r) => r.outcome === f.filter).length }))
    .filter((f) => f.count > 0);
}

/** §9 step 2: the table opens on Errors when there are any, otherwise on Updating — or the first outcome present. */
export function defaultFilter(review: ImportReview): ReviewFilter {
  const present = reviewFilters(review).map((f) => f.filter);
  if (present.includes('error')) return 'error';
  if (present.includes('update')) return 'update';
  return present[0] ?? 'update';
}

/** PI60: where a part stands. `skipped` is a part whose preview had nothing to write (PI56). */
export type PartStatus = 'waiting' | 'checking' | 'checked' | 'saving' | 'saved' | 'skipped' | 'failed' | 'not_attempted';

export interface PartState {
  part: number;
  parts: number;
  firstRow: number;
  lastRow: number;
  status: PartStatus;
  /** Why it failed, in words. */
  reason: string | null;
}

/** The two routes, as the run calls them. The modal passes `api.players.importPreview` / `importCommit` bound to the room. */
export interface RunApi {
  preview(body: PreviewPlayerImportRequest): Promise<PlayerImportPreview>;
  commit(body: CommitPlayerImportRequest): Promise<PlayerImportResult>;
}

export type CheckOutcome =
  | { kind: 'reviewed'; review: ImportReview }
  | { kind: 'cancelled' }
  | { kind: 'failed'; reason: string };

export type SaveOutcome =
  | { kind: 'done'; written: PlayerImportCounts }
  | { kind: 'stopped'; written: PlayerImportCounts; cancelled: boolean };

export interface ImportRun {
  readonly importId: string;
  parts(): PartState[];
  check(): Promise<CheckOutcome>;
  save(): Promise<SaveOutcome>;
  cancel(): void;
}

export interface RunOptions {
  importId: string;
  /** Turns a refused request into words — `describeApiError` in the modal. */
  describe: (error: unknown) => string;
  /** Told after every status change, so the modal re-renders. */
  onChange?: () => void;
  /** Told once per part that landed, so the screen refreshes what it changed (§9) — never for one refused or skipped. */
  onSaved?: (part: number) => void;
}

/** PI60: the line above the part list, naming the part in hand; null when none is. */
export function progressLine(parts: readonly PartState[]): string | null {
  const inHand = parts.find((p) => p.status === 'checking' || p.status === 'saving');
  if (!inHand) return null;
  return `${inHand.status === 'checking' ? 'Checking' : 'Saving'} part ${inHand.part} of ${inHand.parts}…`;
}

const STATUS_WORDS: Record<PartStatus, string> = {
  waiting: 'waiting',
  checking: 'checking',
  checked: 'checked',
  saving: 'saving',
  saved: 'saved',
  skipped: 'nothing to change',
  failed: 'failed',
  not_attempted: 'not attempted',
};

/**
 * PI60: one part of the list — its rows and where it stands, a failed one with its reason, unless the summary above it
 * already gives the reason (§9 step 4), which the list would only repeat.
 */
export function partLine(part: PartState, reasonSaidAbove = false): string {
  const status = part.status === 'failed' && part.reason && !reasonSaidAbove ? `failed: ${part.reason}` : STATUS_WORDS[part.status];
  return `Part ${part.part} of ${part.parts} · rows ${formatRow(part.firstRow)}–${formatRow(part.lastRow)} · ${status}`;
}

const numbered = (parts: readonly PartState[]) => listPhrase(parts.map((p) => String(p.part)));
const sentence = (text: string) => (/[.!?]$/.test(text) ? text : `${text}.`);
/** A reason continuing a sentence: its first letter lowered, unless the word is in capitals (an acronym). */
const lowerFirst = (text: string) => (/^[A-Z][a-z]/.test(text) ? text[0].toLowerCase() + text.slice(1) : text);

/**
 * §9 step 4: what the run says when it ends. A stopped run — refused, or Cancel during saving (PI62) — names the
 * parts that landed, the one that did not and why, and the ones not attempted, and says the same file finishes
 * it (PI56); one that saved nothing says so in one line, with the reason, and has nothing to finish. A finished run says what it wrote, and where conflicts are resolved. Never a contact value: the
 * counts are all it has (PI28).
 */
export function runSummary(parts: readonly PartState[], outcome: SaveOutcome): string[] {
  const of = (status: PartStatus) => parts.filter((p) => p.status === status);
  const saved = of('saved');
  const skipped = of('skipped');
  const skippedLine = skipped.length === 0 ? ''
    : ` ${skipped.length === 1 ? 'Part' : 'Parts'} ${numbered(skipped)} had nothing to change.`;

  if (outcome.kind === 'done') {
    const w = outcome.written;
    const lines = [
      `${parts.length === 1 ? 'The import was saved.' : `All ${parts.length} parts were saved.`}${skippedLine}`,
      `Saved: ${w.created.toLocaleString('en-US')} new · ${w.updated.toLocaleString('en-US')} updated · `
        + `${plural(w.contactsFilled, 'field filled', 'fields filled')} · ${plural(w.conflicts, 'conflict', 'conflicts')}.`,
    ];
    if (w.conflicts > 0) lines.push('Conflicts kept the room’s contact; open a card on this screen to change it.');
    return lines;
  }

  const failed = of('failed');
  const notAttempted = of('not_attempted');
  // Nothing landed: one line, and nothing to finish — running the file again is simply the next import.
  if (saved.length === 0) {
    const reason = failed[0]?.reason;
    const nothing = reason ? `Nothing was saved: ${sentence(lowerFirst(reason))}` : 'Nothing was saved.';
    return [`${outcome.cancelled ? 'Stopped. ' : ''}${nothing}${skippedLine}`];
  }
  const said = [
    `${saved.length === 1 ? 'Part' : 'Parts'} ${numbered(saved)} of ${parts.length} ${saved.length === 1 ? 'was' : 'were'} saved.`,
    ...failed.map((p) => sentence(`Part ${p.part} was not: ${p.reason ?? 'it was refused'}`)),
    ...(notAttempted.length === 0 ? []
      : [`${notAttempted.length === 1 ? 'Part' : 'Parts'} ${numbered(notAttempted)} ${notAttempted.length === 1 ? 'was' : 'were'} not attempted.`]),
  ].join(' ');
  return [
    `${outcome.cancelled ? 'Stopped. ' : ''}${said}${skippedLine}`,
    'Run this file again to finish: what was saved will show as unchanged.',
  ];
}

/** §9 step 4's step bar: whether Import may show as done — never when the run stopped with nothing saved. */
export function importReached(parts: readonly PartState[], outcome: SaveOutcome): boolean {
  return outcome.kind === 'done' || parts.some((p) => p.status === 'saved');
}

/**
 * §9 step 4: a stopped run offers **Review again** — a new run of the same file under a new import id, which is
 * how a refused part is answered (PI56), the three 409s included; never by sending the part again.
 */
export function offersReviewAgain(outcome: SaveOutcome): boolean {
  return outcome.kind === 'stopped';
}

const CANCELLED = Symbol('cancelled');

/** PI77's words, verbatim. */
const TEMPLATE_CHANGED_WHILE_CHECKING = "This room's players template changed while the file was being checked. Review it again.";

/**
 * Why a part's commit was refused, in words (§9 step 3). The three 409s are the import's own: the part is not what
 * was reviewed — its cards, its rows or the room's players template (PI76) — so the remedy is a new review, never
 * sending it again.
 */
export function refusalReason(error: unknown, describe: (error: unknown) => string): string {
  const code = (error as { code?: string } | null)?.code;
  if (code === 'import_stale') return 'Somebody changed one of these cards since this review.';
  if (code === 'import_file_changed') return 'The part sent is not the one reviewed.';
  if (code === 'import_template_changed') return "This room's players template changed since the review. Review the file again.";
  return describe(error);
}

/**
 * A run of one file: its parts checked in order (PI53), then saved in order (PI54). It holds the previews, so a
 * save sends each part exactly as it was checked, with the hashes its preview answered (PI37, PI38) — and never
 * retries a refused part: a 409 means the part is not what was reviewed, and only a new review can say what it is.
 */
export function createImportRun(file: ImportFile, parts: readonly ImportPart[], api: RunApi, options: RunOptions): ImportRun {
  const states: PartState[] = parts.map((p) => ({
    part: p.part, parts: p.parts, firstRow: p.firstRow, lastRow: p.lastRow, status: 'waiting', reason: null,
  }));
  const previews: PlayerImportPreview[] = [];
  let review: ImportReview | null = null;
  let cancelRequested = false;
  let abandon: (() => void) | null = null;

  const set = (index: number, status: PartStatus, reason: string | null = null) => {
    states[index] = { ...states[index], status, reason };
    options.onChange?.();
  };
  const notAttemptedFrom = (index: number) => {
    for (let i = index; i < states.length; i += 1) set(i, 'not_attempted');
  };
  const body = (p: ImportPart) => ({ importId: options.importId, part: p.part, parts: p.parts, firstRow: p.firstRow, rows: p.rows });

  /** The preview, unless Cancel arrives first: then the answer, whenever it comes, is not waited for (PI61). */
  const abandonable = <T>(request: Promise<T>) =>
    Promise.race([
      request,
      new Promise<typeof CANCELLED>((resolve) => {
        abandon = () => resolve(CANCELLED);
      }),
    ]);

  return {
    importId: options.importId,
    parts: () => states.map((s) => ({ ...s })),

    async check() {
      for (let i = 0; i < parts.length; i += 1) {
        if (cancelRequested) return { kind: 'cancelled' };
        const part = parts[i];
        set(i, 'checking');
        let answer: PlayerImportPreview | typeof CANCELLED;
        try {
          answer = await abandonable(api.preview(body(part)));
        } catch (error) {
          const reason = `Part ${part.part} of ${part.parts} could not be checked: ${options.describe(error)}`;
          set(i, 'failed', reason);
          notAttemptedFrom(i + 1);
          return { kind: 'failed', reason };
        } finally {
          abandon = null;
        }
        if (answer === CANCELLED) return { kind: 'cancelled' };
        // PI77: a part read with another template than the first was read with another set of columns, so the
        // parts no longer describe one file. Stopped as a failed preview stops it: no later part, no commit.
        if (i > 0 && answer.templateHash !== previews[0].templateHash) {
          set(i, 'failed', TEMPLATE_CHANGED_WHILE_CHECKING);
          notAttemptedFrom(i + 1);
          return { kind: 'failed', reason: TEMPLATE_CHANGED_WHILE_CHECKING };
        }
        previews[i] = answer;
        set(i, 'checked');
      }
      // PI51: only now, with every part answered and nothing committed, is the file checked for a repeated card.
      review = checkRepeats(file, previews, combineReview(previews));
      return { kind: 'reviewed', review };
    },

    async save() {
      if (review === null || review.counts.errors > 0)
        throw new Error('Only a run reviewed with no errors can be saved.');
      cancelRequested = false;
      const written = { ...ZERO_COUNTS };
      for (let i = 0; i < parts.length; i += 1) {
        if (cancelRequested) {
          notAttemptedFrom(i);
          return { kind: 'stopped', written, cancelled: true };
        }
        const preview = previews[i];
        if (changeCount(preview.counts) === 0) {
          set(i, 'skipped');
          continue;
        }
        set(i, 'saving');
        try {
          const result = await api.commit({
            ...body(parts[i]),
            fileHash: preview.fileHash,
            stateHash: preview.stateHash,
            templateHash: preview.templateHash,
          });
          for (const key of Object.keys(written) as (keyof PlayerImportCounts)[]) written[key] += result.counts[key];
          set(i, 'saved');
          options.onSaved?.(parts[i].part);
        } catch (error) {
          set(i, 'failed', refusalReason(error, options.describe));
          notAttemptedFrom(i + 1);
          return { kind: 'stopped', written, cancelled: cancelRequested };
        }
      }
      return { kind: 'done', written };
    },

    cancel() {
      cancelRequested = true;
      abandon?.();
    },
  };
}
